import axios, { AxiosError } from 'axios';
import { API_BASE_URL } from 'constants/index';
import { useState } from 'react';
import { login } from 'reducers/authReducer';

export type TRegisterDraftResponseData = {
  data: {
    sms_code: string;
    email: string;
    email_code: string;
    mobile_number: string;
    updated_at: string;
    created_at: string;
    id: number;
  };
  message: string;
  status_code: number;
};

export type TSendOtpResponseData = {
  message: string;
  status_code: number;
};

export type TSubmitRegisterDraftPayload = {
  email: string;
  password: string;
  password_confirmation: string;
  mobile_number: string;
};

export type TSubmitRegisterDraftResponseDataError = {
  error: {
    message: string;
    errors: {
      email: Array<string>;
      mobile_number: Array<string>;
    };
    status_code: number;
    debug: {
      line: number;
      file: string;
      class: string;
      trace: Array<string>;
    };
  };
};

export type TVerifyOtpPayload = {
  sms_code: string;
  email_code: string;
  email: string;
  password: string;
};

export type TVerifyOtpResponseDataError = {
  error: {
    message: string;
    errors: {
      sms_code: Array<string>;
      email_code: Array<string>;
    };
    status_code: number;
  };
};

export type TVerifyOtpResponseData = {
  data: {
    token: string;
  };
  message: string;
  status_code: number;
};

const useRegisterWithOtp = () => {
  const [userRegistrationId, setUserRegistrationId] =
    useState<number | undefined>();
  const [token, setToken] = useState('');
  const [isPosting, setIsPosting] = useState({
    registerDraft: false,
    otpEmail: false,
    otpSms: false,
    verifyOtp: false,
  });

  const submitRegisterDraft = async (payload: TSubmitRegisterDraftPayload) => {
    setIsPosting((prev) => {
      return {
        ...prev,
        registerDraft: true,
      };
    });

    const response = await axios
      .post<TRegisterDraftResponseData>(`${API_BASE_URL}/registers`, payload)
      .then((res) => {
        const responseData = res.data;
        if (responseData.status_code !== 200) {
          return {
            isSuccess: false,
            message: responseData.message,
            data: responseData.data,
            detail: null
          };
        }
        setUserRegistrationId(responseData.data.id);
        return {
          isSuccess: true,
          message: responseData.message,
          data: responseData.data,
          detail: null
        };
      })
      .catch((err: AxiosError<TSubmitRegisterDraftResponseDataError>) => {
        const res = {
          isSuccess: false,
          message:
            'Failed to submit registration, please try again later or contact our customer service',
          data: null,
          detail: null
        };

        const responseError = err.response?.data;
        if (responseError?.error?.errors) {
          Object.assign(res, {
            message: responseError?.error?.message,
            detail: responseError?.error?.errors,
          });
        }
        return res;
      });

    setIsPosting((prev) => {
      return {
        ...prev,
        registerDraft: false,
      };
    });

    return response;
  };

  const sendOtpEmail = async () => {
    setIsPosting((prev) => {
      return {
        ...prev,
        otpEmail: true,
      };
    });

    const response = await axios
      .post<TSendOtpResponseData>(
        `${API_BASE_URL}/otp/send/email/${userRegistrationId}`,
      )
      .then((res) => {
        const responseData = res.data;
        if (responseData.status_code !== 200) {
          return {
            isSuccess: false,
            message: responseData.message,
            data: responseData,
          };
        }

        return {
          isSuccess: true,
          message: responseData.message,
          data: responseData,
        };
      })
      .catch((_err) => {
        return {
          isSuccess: false,
          message:
            'Failed to send OTP to your email, please try again later or contact our customer service',
          data: null,
        };
      });

    setIsPosting((prev) => {
      return {
        ...prev,
        otpEmail: false,
      };
    });

    return response;
  };

  const sendOtpPhoneNumber = async () => {
    setIsPosting((prev) => {
      return {
        ...prev,
        otpPhoneNumber: true,
      };
    });

    const response = await axios
      .post<TSendOtpResponseData>(
        `${API_BASE_URL}/otp/send/sms/${userRegistrationId}`,
      )
      .then((res) => {
        const responseData = res.data;
        if (responseData.status_code !== 200) {
          return {
            isSuccess: false,
            message: responseData.message,
            data: responseData,
          };
        }

        return {
          isSuccess: true,
          message: responseData.message,
          data: responseData,
        };
      })
      .catch((_err) => {
        return {
          isSuccess: false,
          message:
            'Failed to send OTP to your phone number, please try again later or contact our customer service',
          data: null,
        };
      });

    setIsPosting((prev) => {
      return {
        ...prev,
        otpPhoneNumber: false,
      };
    });

    return response;
  };

  const submitVerifyOtp = async (payload: TVerifyOtpPayload) => {
    setIsPosting((prev) => {
      return {
        ...prev,
        verifyOtp: true,
      };
    });

    const response = await axios
      .post<TVerifyOtpResponseData>(
        `${API_BASE_URL}/verify-otp/${userRegistrationId}`,
        payload,
      )
      .then((res) => {
        const responseData = res.data;
        if (responseData.status_code !== 200) {
          return {
            isSuccess: false,
            message: responseData.message,
            data: responseData.data,
            detail: null
          };
        }
        const token = responseData.data.token;
        setToken(token);
        login({ token });

        return {
          isSuccess: true,
          message: responseData.message,
          data: responseData.data,
          detail: null
        };
      })
      .catch((err: AxiosError<TVerifyOtpResponseDataError>) => {
        const res = {
          isSuccess: false,
          message:
            'Failed to verify otp, please try again later or contact our customer service',
          data: null,
          detail: null
        };

        const responseError = err.response?.data;
        if (responseError?.error?.errors) {
          Object.assign(res, {
            message: 'Please correct your input',
            detail: responseError?.error?.errors,
          });
        }
        return res;
      });

    setIsPosting((prev) => {
      return {
        ...prev,
        verifyOtp: false,
      };
    });

    return response;
  };

  return {
    token,
    userRegistrationId,
    isPosting,
    submitRegisterDraft,
    sendOtpEmail,
    sendOtpPhoneNumber,
    submitVerifyOtp,
  };
};

export default useRegisterWithOtp;
